import * as v from 'valibot';
import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { not, or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import RoleDelegationRow from './row.gts';
import RoleDelegationCoveringRow from './covering-row.gts';
import SelectEmployee from '../../select/employee.gts';
import type Store from '@ember-data/store';
import Table from 'tio-reimagined/components/table';
import type EmployeeModel from 'tio-data/models/employee';
import { HStack, Section, VStack } from 'tio-ui/components/layout';
import type Owner from '@ember/owner';
import Button from 'tio-reimagined/components/button';
import ConfirmationDialog from 'tio-reimagined/components/confirmation-dialog';
import { on } from '@ember/modifier';
import type GenericRelationshipModel from 'tio-data/models/generic-relationship';

interface ProfileRoleDelegationSignature {
  Args: {
    employee: EmployeeModel;
    employeeRoute?: string;
    usePersonRoute?: boolean;
    collapsed?: boolean;
    allowRevoke?: boolean;
  };
  Element: HTMLElement;
}

const delegateSchema = v.object({
  selectedDelegateEmployeeId: v.pipe(
    v.string('Please select a delegate'),
    v.minLength(1, 'Please select a delegate')
  ),
  expiresOn: v.pipe(
    v.string('Please select an expiration date'),
    v.minLength(1, 'Please select an expiration date')
  ),
});

export default class ProfileRoleDelegationComponent extends Component<ProfileRoleDelegationSignature> {
  @service declare store: Store;
  // We use null because menus will not rerender if undefined is used
  @tracked selectedDelegateEmployee: EmployeeModel | null = null;
  @tracked expiresOn: string | undefined = undefined;
  @tracked refreshTrigger = 0;
  @tracked pendingRevokeId: string | null = null;

  constructor(owner: Owner, args: ProfileRoleDelegationSignature['Args']) {
    super(owner, args);
  }

  get isFormValid() {
    return !this.formValidationErrors.length;
  }

  get formValidationErrors() {
    const schema = delegateSchema;

    const formModel = {
      selectedDelegateEmployeeId: this.selectedDelegateEmployee?.id,
      expiresOn: this.expiresOn,
    };

    try {
      v.parse(schema, formModel);
    } catch (err) {
      if (err instanceof v.ValiError) {
        return err.issues || [];
      }
      return [];
    }
    return [];
  }

  get jurisdictiveRelationships() {
    return this.args.employee.jurisdictiveRelationships;
  }

  consumeRefreshTrigger = () => {
    return this.refreshTrigger;
  };

  // Querying the data store for records of type 'generic-relationship' and filtering those records
  // based on whether their 'delegateOf' property matches any of the IDs from the 'jurisdictiveRelationships' array
  // of the 'employee' object.
  delegationData = trackedFunction(this, async () => {
    this.consumeRefreshTrigger();
    const jurisdictiveRelationshipIds = this.jurisdictiveRelationships.map((rel) => rel.id);

    if (jurisdictiveRelationshipIds.length === 0) {
      return [];
    }

    const data = (await this.store.query('generic-relationship', {
      filter: {
        delegateOf: jurisdictiveRelationshipIds,
      },
    })) as GenericRelationshipModel[];

    return data;
  });

  get delegationHistory() {
    return this.delegationData.value || [];
  }

  get coveringHistory() {
    return (
      this.args.employee.jurisdictiveRelationships?.filter(
        (rel) => rel.delegateOf?.id && rel.delegateOf.id !== rel.id
      ) ?? []
    );
  }

  submitDelegation = () => {
    if (!this.isFormValid) {
      return;
    } else {
      this.submitTask.perform();
    }
  };

  submitTask = dropTask(async () => {
    if (!this.selectedDelegateEmployee || !this.expiresOn) {
      return;
    }
    const governorType = 'Employee';

    try {
      await this.store
        .adapterFor('generic-relationship')
        .delegateGovernorRelationship(
          this.selectedDelegateEmployee.id,
          governorType,
          this.expiresOn,
          this.args.employee.id,
          governorType
        );
      this.resetSelection();
      await this.args.employee.reload();
      this.refreshTrigger++;
    } catch (e) {
      console.error(e);
    }
  });

  promptRevoke = (delegateId: string) => {
    this.pendingRevokeId = delegateId;
  };

  cancelRevoke = () => {
    this.pendingRevokeId = null;
  };

  confirmRevoke = dropTask(async () => {
    if (!this.pendingRevokeId) return;
    try {
      await this.store
        .adapterFor('generic-relationship')
        .deleteJurisdictiveRelationship(this.pendingRevokeId);
      this.pendingRevokeId = null;
      await this.args.employee.reload();
      this.refreshTrigger++;
    } catch (e) {
      console.error(e);
    }
  });

  resetSelection = () => {
    this.selectedDelegateEmployee = null;
    this.expiresOn = undefined;
  };

  setSelectedEmployeeFilter = (employee?: EmployeeModel) => {
    this.selectedDelegateEmployee = employee || null;
  };

  formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  handleDateChange = (selectedDates: Date[]) => {
    if (selectedDates[0]) {
      this.expiresOn = this.formatLocalDate(selectedDates[0]);
    } else {
      this.expiresOn = undefined;
    }
  };

  <template>
    <Section @collapsed={{@collapsed}}>
      <:header>{{t "common.profile.role_delegation.default"}}</:header>
      <:body>
        <VStack>
          <p>{{t "common.profile.role_delegation.description"}}</p>
          <HStack class="md:items-end">
            <SelectEmployee
              @label="Designate Roles Temporarily to Employee"
              @selected={{this.selectedDelegateEmployee}}
              @onChange={{this.setSelectedEmployeeFilter}}
              {{! @glint-expect-error}}
              @companyId={{@employee.company.id}}
              @showPayrollId={{true}}
              @placeholder="Search (Name, Payroll ID, Email)"
              class="w-80"
            />
            <div>
              <label for="date-expires" class="text-xs text-gray-500 font-semibold text-foreground">
                {{t "common.profile.role_delegation.date_expires"}}
              </label>
              {{! @glint-expect-error: EmberFlatpickr type overrides}}
              <EmberFlatpickr
                @date={{or this.expiresOn ""}}
                @onChange={{this.handleDateChange}}
                @dateFormat="Y-m-d"
                @altInput={{true}}
                @altFormat="F j, Y"
                @minDate="today"
                placeholder="Select date"
                class="tio-form-input"
              />
            </div>
            <div>
              <Button
                {{on "click" this.submitDelegation}}
                @isDisabled={{not this.isFormValid}}
                @isRunning={{this.submitTask.isRunning}}
                class="self-end"
              >
                {{t "common.save"}}
              </Button>
            </div>
          </HStack>

          <Table @isLoading={{false}}>
            <:thead as |options|>
              <options.tr>
                <options.th>{{t "common.profile.role_delegation.delegate_name"}}</options.th>
                <options.th>{{t "common.profile.role_delegation.relationship_type"}}</options.th>
                <options.th>{{t "common.profile.role_delegation.date_assigned"}}</options.th>
                <options.th>{{t "common.profile.role_delegation.status"}}</options.th>
                <options.th>{{t "common.profile.role_delegation.date_expires"}}</options.th>
                {{#if @allowRevoke}}
                  <options.th />
                {{/if}}
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each this.delegationHistory as |delegate|}}
                <RoleDelegationRow
                  @delegate={{delegate}}
                  @cellClass={{options.cellClass}}
                  @employeeRoute={{@employeeRoute}}
                  @usePersonRoute={{@usePersonRoute}}
                  @onRevoke={{if @allowRevoke (fn this.promptRevoke delegate.id)}}
                />
              {{else}}
                <options.tr>
                  <options.td colspan="5">
                    {{t "common.profile.role_delegation.none_delegated"}}
                  </options.td>
                </options.tr>
              {{/each}}
            </:tbody>
          </Table>

          {{#if this.coveringHistory.length}}
            <Table @isLoading={{false}}>
              <:thead as |options|>
                <options.tr>
                  <options.th colspan="5">{{t
                      "common.profile.role_delegation.covering_roles_default"
                    }}</options.th>
                </options.tr>
                <options.tr>
                  <options.th>{{t "common.profile.role_delegation.covered_employee"}}</options.th>
                  <options.th>{{t "common.profile.role_delegation.relationship_type"}}</options.th>
                  <options.th>{{t "common.profile.role_delegation.date_assigned"}}</options.th>
                  <options.th>{{t "common.profile.role_delegation.status"}}</options.th>
                  <options.th>{{t "common.profile.role_delegation.date_expires"}}</options.th>
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each this.coveringHistory as |coveringRole|}}
                  <RoleDelegationCoveringRow
                    @coveringRole={{coveringRole}}
                    @cellClass={{options.cellClass}}
                    @employeeRoute={{@employeeRoute}}
                    @usePersonRoute={{@usePersonRoute}}
                  />
                {{/each}}
              </:tbody>
            </Table>
          {{/if}}

          {{#if @allowRevoke}}
            <ConfirmationDialog
              @isOpen={{if this.pendingRevokeId true false}}
              @title="Revoke Delegation"
              @intent="danger"
              @confirmText="Revoke"
              @isRunning={{this.confirmRevoke.isRunning}}
              @onConfirm={{this.confirmRevoke.perform}}
              @onCancel={{this.cancelRevoke}}
            >
              Are you sure you want to revoke this delegation? The delegate will immediately lose
              access to the delegated roles.
            </ConfirmationDialog>
          {{/if}}
        </VStack>
      </:body>
    </Section>
  </template>
}
